import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get as _get } from "lodash";
import { BigNumber } from "bignumber.js";
// components mui
import {
  Box,
  InputAdornment,
  Modal,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Switch,
} from "@mui/material";

// Actions
import { setModalData, setModal } from "../../../redux/actions/modals";
import { setExplorer, setSlippage, setUseAPI } from "../../../redux/actions/settings";

// components
import { EXPLORERS } from "../../../utils/explorer";
import { useExplorer } from "../../../hooks/useExplorer";
import ModalHeader from "./../ModalHeader";

// helpers
import LS from "./../../../helpers/storage";

// slippage in percent: above 0 and below 50, a warning above 5
const MAX_SLIPPAGE = 50;
const HIGH_SLIPPAGE = 5;
const isValidSlippage = (value) => {
  const slippage = new BigNumber(value);
  return slippage.isFinite() && slippage.isGreaterThan(0) && slippage.isLessThan(MAX_SLIPPAGE);
};

const ModalSettingsApp = () => {
  // Dispatch to call actions
  const dispatch = useDispatch();

  // selectors
  const settingSelector = useSelector((state) => state.settings);
  const currentModal = useSelector((state) => state.modals.modal);

  // state
  const slippage = _get(settingSelector, "slippage", "0.10");
  const useapi = _get(settingSelector, "useapi", true);
  const explorer = useExplorer();

  // text being typed in the custom field, null shows the saved slippage
  const [draft, setDraft] = useState(null);
  const invalidDraft = draft !== null && !isValidSlippage(draft);

  const saveSlippage = (value) => {
    LS.settings({ slippage: value });
    dispatch(setSlippage(value));
  };

  const handlePreset = (e, value) => {
    if (!value) return;
    setDraft(null);
    saveSlippage(value);
  };

  const handleInput = (e) => {
    const text = String(e.target.value).replace(",", ".");
    if (!/^\d*\.?\d*$/.test(text)) return;
    setDraft(text);
    if (isValidSlippage(text)) {
      saveSlippage(text);
    }
  };

  const closeModal = () => {
    dispatch(setModal(null));
    dispatch(setModalData(null));
  };

  const handleExplorer = (e, explorerId) => {
    if (!explorerId) return;
    LS.settings({ explorer: explorerId });
    dispatch(setExplorer(explorerId));
  };

  const handleChangeUseApi = (e) => {
    let useApiValue = e.target.checked;
    LS.settings({ useapi: useApiValue });
    dispatch(setUseAPI(useApiValue));
  };

  return (
    <Modal open={currentModal === "SettingsApp"} onClose={closeModal} sx={{ display: "flex", p: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 420, m: "auto" }}>
        <ModalHeader title={"Settings"} closeFunction={closeModal} />

        <Box sx={{ px: 2.5, pb: 2.5, pt: 1, display: "flex", flexDirection: "column", gap: 3 }}>
          <Box component="section" aria-labelledby="settings-slippage">
            <Typography id="settings-slippage" sx={{ fontWeight: 600 }}>
              Slippage tolerance
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, mb: 1.5 }}>
              The swap reverts if the price moves more than this.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <ToggleButtonGroup
                size="small"
                value={slippage}
                exclusive
                onChange={handlePreset}
                aria-label="slippage presets"
              >
                {["0.05", "0.10", "0.20"].map((value) => (
                  <ToggleButton key={value} value={value} sx={{ px: 1.5, height: 40 }}>
                    {value}%
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <TextField
                size="small"
                sx={{ flex: 1, "& .MuiOutlinedInput-root": { height: 40 } }}
                slotProps={{
                  htmlInput: { inputMode: "decimal", "aria-label": "custom slippage in percent" },
                  input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
                }}
                value={draft !== null ? draft : slippage}
                onChange={handleInput}
                onBlur={() => setDraft(null)}
                error={invalidDraft}
                helperText={
                  invalidDraft
                    ? `Between 0 and ${MAX_SLIPPAGE}`
                    : Number(slippage) > HIGH_SLIPPAGE
                      ? "High slippage: you may get a worse price"
                      : ""
                }
              />
            </Box>
          </Box>

          <Box
            component="section"
            sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}
          >
            <Box>
              <Typography id="settings-api" sx={{ fontWeight: 600 }}>
                Use KoinDX API
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                Faster prices and positions. Turn it off to read everything from the Koinos nodes.
              </Typography>
            </Box>
            <Switch
              checked={useapi}
              onChange={handleChangeUseApi}
              slotProps={{ input: { "aria-labelledby": "settings-api" } }}
            />
          </Box>

          {EXPLORERS.length > 1 ? (
            <Box component="section" aria-labelledby="settings-explorer">
              <Typography id="settings-explorer" sx={{ fontWeight: 600 }}>
                Block explorer
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, mb: 1.5 }}>
                Transactions, addresses and blocks open in this explorer.
              </Typography>
              <ToggleButtonGroup
                size="small"
                fullWidth
                value={explorer.id}
                exclusive
                onChange={handleExplorer}
                aria-labelledby="settings-explorer"
              >
                {EXPLORERS.map((option) => (
                  <ToggleButton key={option.id} value={option.id} sx={{ height: 40 }}>
                    {option.name}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalSettingsApp;
